import { saveAs } from "file-saver";
import ServiceUtil from "@ty/service_util";
import { defaultsDeep, isUndefined, merge, omitBy } from "lodash-es";
import { joinPaths } from "urijs";
import compact from "lodash-es/compact";
import { fetchEventSource } from "@fortaine/fetch-event-source";
import Vue from "vue";
import { getAppInstance } from "@/utils/app";
import { openLogin } from "@/utils/login";

const { tycom } = window;
let serviceUtil;
let defaultApiGateway;
const tyApp = getAppInstance();

const RESP_CODE_SUCCESS = 20000;
const ACCESS_DENIED_CODE = "access_denied";

async function initServiceUtil() {
  if (serviceUtil) {
    return;
  }
  await tycom._initConfigPromise;
  const secretId = tycom.configCenter.getEnv("BUSI_SECRET");
  serviceUtil = new ServiceUtil({ secretId });
  defaultApiGateway =
    import.meta.env.VITE_DEFAULT_API_GATEWAY ||
    tycom.configCenter.getEnv("UC_API_GATEWAY");
}

function genServiceOptions({
  actionUrl,
  serviceName,
  apiGateway = defaultApiGateway,
  serviceVersion = "1.0.0",
}) {
  return {
    apiGateway,
    serviceName,
    actionUrl,
    serviceVersion,
  };
}

async function onResponse(promise) {
  let res;
  const defaultErrMsg = "服务异常";
  try {
    res = await promise;
  } catch (e) {
    console.error(e);
    const _code = e.code;
    if (_code === "ERR_CANCELED") {
      throw "ERR_CANCELED";
    }
    const code = e?.response?.data?.code;
    const msg = e?.response?.data?.msg || defaultErrMsg;
    if (code === ACCESS_DENIED_CODE) {
      openLogin();
    }
    throw new Error(msg);
  }
  if (Object.prototype.toString.call(res) === "[object Blob]") {
    return res;
  } else if (res.code !== RESP_CODE_SUCCESS && !res.data) {
    throw new Error(res.message || defaultErrMsg);
  } else {
    return res.data;
  }
}

async function request({ method, url, noMessage, data, params, ...args }) {
  if (!method) throw new Error("缺少method");
  let token;
  if (tyApp && !import.meta.env.VITE_SKIP_APP_TOKEN) {
    const isConnected = await tyApp.nsCall({
      method: "getConnected",
    });
    if (isConnected === false) {
      await tyApp.nsCall({
        method: "showNetworkError",
      });
      throw new Error("网络异常");
    }
    const t = setTimeout(() => {
      console.error(new Error("超过5秒没有返回token"));
    }, 5000);
    token = await tyApp
      .nsCall({
        method: "getToken",
      })
      .catch(console.error);
    clearTimeout(t);
    // token = await Promise.race([
    //   tyApp
    //     .nsCall({
    //       method: "getToken",
    //     })
    //     .catch(console.error),
    //   new Promise((resolve, reject) => {
    //     setTimeout(() => {
    //       reject(new Error("获取app token超时"));
    //     }, 5000);
    //   }),
    // ]);
  }
  let headers = {};
  if (token) {
    headers["app-token"] = token;
  }
  headers = omitBy(headers, isUndefined); //去掉所有undefined
  defaultsDeep(args, {
    timeout: 1000 * 15,
    headers,
  });
  await initServiceUtil();
  if (method.toLowerCase() === "get") {
    return onResponse(
      serviceUtil.get(
        genServiceOptions({
          actionUrl: url,
          ...args,
        }),
        params,
        args
      ),
      noMessage
    );
  } else if (method.toLowerCase() === "post") {
    const headers = args?.headers
      ? args?.headers
      : {
          "content-type": "application/json; charset=utf-8",
        };
    return onResponse(
      serviceUtil.post(
        genServiceOptions({
          actionUrl: url,
          ...args,
        }),
        params,
        data,
        merge(
          args,
          import.meta.env.PROD
            ? {
                headers,
              }
            : undefined
        )
      ),
      noMessage
    );
  } else {
    throw new Error(`不支持的method：${method}`);
  }
}

async function getUrl({ url, ...args }) {
  const opt = genServiceOptions({
    actionUrl: url,
    ...args,
  });
  await initServiceUtil();
  return await serviceUtil.getUrl(
    opt.apiGateway,
    opt.serviceName,
    opt.actionUrl,
    opt.serviceVersion
  );
}
const downloadFileOptions = {
  responseType: "blob",
  transformResponse: [
    async function (blob, headers) {
      const getFileName = () => {
        if (headers?.["content-disposition"]) {
          return decodeURI(
            /filename="?([^"]*)/g.exec(headers["content-disposition"])?.[1]
          );
        }
      };
      const fileName = getFileName();
      if (fileName) {
        saveAs(blob, fileName);
        return {
          code: RESP_CODE_SUCCESS,
        };
      } else if (blob?.type?.includes?.("application/json")) {
        return JSON.parse(await blob.text());
      } else {
        return blob;
      }
    },
  ],
};

export async function requestStream({
  url,
  method = "POST",
  data,
  serviceName,
  ...options
}) {
  const input = joinPaths(
    ...compact([
      await window.tycom.configCenter.getEnv("UC_API_GATEWAY"),
      serviceName,
      url,
    ])
  ).toString();
  return new Promise((resolve, reject) => {
    fetchEventSource(input, {
      method,
      body: data && JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
      async onopen(res) {
        const contentType = res.headers.get("content-type");
        if (!res.ok || res.status !== 200) {
          console.error(res);
          reject(new Error("服务异常"));
          throw new Error("服务异常");
        } else if (contentType !== "stream") {
          const response = await res.clone().json();
          const { code, message } = response || {};
          if (code !== RESP_CODE_SUCCESS) {
            reject(new Error(message || "Error"));
            throw new Error(message || "Error");
          }
        }
      },
      onerror(err) {
        // Message({
        //   message: err,
        //   type: "error",
        //   duration: 5 * 1000,
        //   showClose: true,
        // });
        Vue.prototype.$message.fail(err);
        reject(new Error(err));
        throw new Error(err);
      },
      ...options,
    });
  });
}

export default request;

export { getUrl, downloadFileOptions };
